import React from "react";
import {routingAddresses} from "../../../../helpers/routing";
import {RouteComponentProps, withRouter} from "react-router";
import i18n from "../../../../i18n/i18n";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../common/models/httpError";
import {UnCodeModel, UnCodeStatusModel} from "../../../models/unCode";
import { ValidatedValue } from "../../../../helpers/validatedValue";
import Validator from "../../../../helpers/validator";
import {errorMessages} from "../../../../helpers/errorMessage";
import DropDown from "../../../../common/components/shared/dropDown/dropDown";
import TextInput from "../../../../common/components/shared/textInput/textInput";
import { Api } from "../../../../helpers/api";
import { HttpError } from "../../../../common/components/shared/httpError/httpError";
import { UnCodeService } from "../../../services/unCodeService";
import ImdgClass from "../../../../helpers/imdgClass";
import Row from "../../../../common/components/shared/row/row";
import Form from "../../../../common/components/shared/form/form";
import { Utils } from "../../../../helpers/utils";

interface UnCodeFormState {
    isSubmitButtonBlocked: boolean;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
}

interface UnCodeFormProps extends RouteComponentProps {
    unCodeData?: UnCodeModel | null;
    isUpdate: boolean;
}

class UnCodeForm extends React.Component <UnCodeFormProps, UnCodeFormState> {
    private status: ValidatedValue<UnCodeStatusModel>;
    private imdgClass: ValidatedValue<string>;
    private codeUn: ValidatedValue<string>;
    private name: ValidatedValue<string>;

    constructor(props: any) {
        super(props);

        const unCodeValidators = [{
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        }];

        this.status = new ValidatedValue(UnCodeStatusModel.ACTIVE, unCodeValidators);
        this.imdgClass = new ValidatedValue("", unCodeValidators);
        this.codeUn = new ValidatedValue("", unCodeValidators);
        this.name = new ValidatedValue("", unCodeValidators);

        this.state = {
            isSubmitButtonBlocked: false,
            response: undefined
        };
    }

    async componentDidMount() {
        this.setUnCodeData();
    }

    render() {
        return (
            <Form
                renderError={this.renderErrors()}
                renderContent={this.renderContent()}
                isSubmitButtonBlocked={this.state.isSubmitButtonBlocked}
                backAddress={routingAddresses.dictionaries.unCodes.list}
                submit={this.submit.bind(this)}
            />
        );
    }

    renderContent() {
        return (
            <>
                <Row class="flex-type gap-30">
                    <DropDown className="element" label={i18n.t("dictionaries.unCodes.headers.status")}
                        validatedValue={this.status} items={this.setStatusDropDownData()}
                        testId="unCode-status"/>
                    <DropDown className="element" label={i18n.t("dictionaries.unCodes.headers.imdgClass")}
                        validatedValue={this.imdgClass} items={ImdgClass.getImdgClassDropdownItems()}
                        testId="unCode-imdgClass"/>
                    <TextInput className="element" label={i18n.t("dictionaries.unCodes.headers.code")} validatedValue={this.codeUn}
                        testId="unCode-number"/>
                </Row>
                <Row class="flex-type gap-30">
                    <TextInput className="element" label={i18n.t("dictionaries.unCodes.headers.name")} validatedValue={this.name}
                        testId="unCode-name"/>
                </Row>
            </>
        );
    }

    renderErrors() {
        if(!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={this.props.isUpdate ? i18n.t("dictionaries.unCodes.errors.modifyErrorSuffix") : i18n.t("dictionaries.unCodes.errors.addErrorSuffix")}
                    translatedFields={{
                        code: i18n.t("dictionaries.unCodes.httpErrors.code"),
                        name: i18n.t("dictionaries.unCodes.httpErrors.name")
                    }}
                    translatedFieldsForEmpty={{
                        status: i18n.t("dictionaries.unCodes.httpErrors.statusForEmpty"),
                        imdgClass: i18n.t("dictionaries.unCodes.httpErrors.imdgClassForEmpty"),
                        code: i18n.t("dictionaries.unCodes.httpErrors.codeForEmpty"),
                        name: i18n.t("dictionaries.unCodes.httpErrors.nameForEmpty")
                    }}
                    object={i18n.t("dictionaries.unCodes.httpErrors.object")}
                />
            );
        }
    }

    public get isValid() {
        this.imdgClass.validate();
        this.codeUn.validate();
        this.name.validate();
        this.status.validate();

        return this.imdgClass.isValid && this.codeUn.isValid && this.name.isValid && this.status.isValid;
    }

    private setStatusDropDownData(){
        return Object.keys(UnCodeStatusModel).map(value => ({ id: value, name: i18n.t(`activeStatus.${value}`) }));
    }

    private setUnCodeData() {
        this.name.setValue(this.props.unCodeData?.name || "");
        this.codeUn.setValue(this.props.unCodeData?.code || "");
        this.imdgClass.setValue(this.props.unCodeData?.imdgClass || "");
        this.status.setValue(this.props.unCodeData?.status || UnCodeStatusModel.ACTIVE);
    }

    private async submit() {
        this.setState({response: undefined});

        if (this.isValid) {
            this.setState({isSubmitButtonBlocked: true});
            await this.addOrModifyUnCode();
            this.setState({isSubmitButtonBlocked: false});

            if (Api.isSuccess(this.state.response)) {
                this.props.history.push(routingAddresses.dictionaries.unCodes.list);
            }
        }
        Utils.scrollToFirstErrorMessage();
    }

    private async addOrModifyUnCode(){
        if (this.props.isUpdate && this.props.unCodeData?.id) {
            this.setState({
                response: await UnCodeService.modifyUnCode({
                    code: this.codeUn.value.trim(),
                    imdgClass: this.imdgClass.value,
                    name: this.name.value.trim(),
                    status: this.status.value
                }, this.props.unCodeData?.id)
            });
        } else {
            this.setState({
                response: await UnCodeService.addUnCode({
                    code: this.codeUn.value.trim(),
                    imdgClass: this.imdgClass.value,
                    name: this.name.value.trim(),
                    status: this.status.value
                })
            });
        }
    }
}

export default withRouter(UnCodeForm);
